import React, { useMemo , useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useUIConfig, useContext } from 'hooks/useURL'
import ScreenSubmenu from './ScreenSubmenu.jsx'
import WindowSubmenu from './WindowSubmenu.jsx'
import FavoritesSubmenu from './FavoritesSubmenu.jsx'
import { push } from 'Helper/routeHelpers'
import WorkflowSubmenu from './WorkflowSubmenu.jsx'
import userHasPrivs from 'Helper/Auth/userHasPrivs'
import * as authActions from 'actions/authActions'
import DashboardScreenSubmenu from './DashboardScreenSubmenu.jsx'
import DesignModeSubmenu from './DesignModeSubmenu.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AdminDisplayScreenSubmenu from './AdminDisplayScreenSubmenu.jsx'
import HelpSubmenu from './HelpSubmenu.jsx'
import NotificationBadge from '../Notifications/NotificationBadge.jsx'
import debug from 'Helper/debugHelpers'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import SetLocationSubMenu from './SetLocationSubMenu.jsx'
import rootActions from 'actions/rootActions'

const MainDropdown = ({ inWorkflow, prefix }) => {
  const dispatch = useDispatch()
  const viewDesignModeSubmenu = userHasPrivs('ui_design_mode')
  // privileges for which menus show
  const adminMenuAllowed = useMemo(() =>
    userHasPrivs([
      'sys_admin',
      'extract_create',
      'extract_read',
      'extract_update',
      'extract_delete',
      'profile_create',
      'profile_read',
      'profile_update',
      'profile_delete',
      'inventory_create',
      'inventory_read',
      'inventory_update',
      'inventory_delete',
      'patient_flag_create',
      'patient_flag_read',
      'patient_flag_update',
      'patient_flag_delete',
      'treatment_plan_create',
      'treatment_plan_read',
      'treatment_plan_update',
      'treatment_plan_delete',
      'account_create',
      'account_read',
      'account_update',
      'account_delete',
      'question_create',
      'question_read',
      'question_update',
      'question_delete',
      'questionnaire_create',
      'questionnaire_read',
      'questionnaire_update',
      'questionnaire_delete',
      'message_create',
      'message_read',
      'message_update',
      'message_delete',
      'provider_create',
      'provider_read',
      'provider_update',
      'provider_delete',
      'all_user_create',
      'all_user_read',
      'all_user_update',
      'all_user_delete',
      'user_read',
      'user_update',
      'clinic_read',
      'clinic_create',
      'clinic_update',
      'clinic_delete',
      'pharmacy_profile_view',
      'pharmacy_extract_view',
      'st_protocol_read',
      'st_protocol_update',
      'st_protocol_delete',
      'st_panels_read',
      'st_panels_update',
      'st_panels_delete',
      'ent_dilution_type_read',
      'view_click_log',
      'view_api_log',
      'database_backups_read',
      'prescription_panels_read',
      'vials_read',
      'view_visit_history'
    ])
  )

  const lobbyLoginClick = e => {
    if (!inWorkflow) {
      e.preventDefault()
      push('/lobby')
    }
  }

  const {
    windows: allContextWindows,
    screens: allContextScreens,
    workflows: allContextWorkflows
  } = useUIConfig()
  const windows = useMemo(
    () => allContextWindows.filter(win => win.selectable),
    [allContextWindows]
  )
  const screens = useMemo(
    () => allContextScreens.filter(screen => screen.selectable),
    [allContextScreens]
  )
  const workflows = useMemo(
    () => allContextWorkflows.filter(workflow => workflow.selectable),
    [allContextWorkflows]
  )
  const context = useContext()

  const lobbyDashboardClick = e => {
    if (!inWorkflow) {
      e.preventDefault()
      push('/lobby/dashboard')
    }
  }

  const externalLinkClick = (e, link) => {
    e.preventDefault()
    if (!inWorkflow) {
      const newWindow = window.open(link, '_blank', 'noopener,noreferrer')
      if (newWindow) {
        newWindow.opener = null
      }
    }
  }

  const configClick = e => {
    if (!inWorkflow && !settingsDisabled) {
      e.preventDefault()
      push('config')
    }
  }
  const userDetailsClick = e => {
    if (!inWorkflow) {
      e.preventDefault()
      dispatch({
        type: 'UPDATE_WIDGET',
        window: {
          ref: 'userDetails',
          x: '2%',
          y: '2%',
          width: '96%',
          height: '96%'
        }
      })
    }
  }
  const userMessagesClick = e => {
    if (!inWorkflow) {
      e.preventDefault()
      dispatch({
        type: 'UPDATE_WIDGET',
        window: {
          ref: 'userMessages',
          x: '2%',
          y: '2%',
          width: '96%',
          height: '96%'
        }
      })
    }
  }
  const loginClick = e => {
    e.preventDefault()
    debug.log(
      'doing logout due to user menu -> logout click',
      debug.logLevels.AUTH
    )
    dispatch(authActions.doLogout())
  }

  const isIpad = getCurrentScreenType() === 'ipad'
  const pathname = useSelector(state => state.router.location.pathname)
  const dashboardDisabled = pathname.indexOf('/workflow/') !== -1
  const adminDisplayDisabled = pathname.indexOf('/workflow/') !== -1
  const settingsDisabled = pathname.indexOf('/config') !== -1 || isIpad
  const username = useSelector(state => state.auth.username)
  const externalLinks =
    useSelector(state => state.appConfig?.menu_config?.external_links) || []

  const clinicLocation = useSelector(state => state.appConfig.CLINIC_LOCATION)
  const clinics = useSelector(state => state.appConfig.clinics) || []

// Ensure clinic list is loaded
useEffect(() => {
  if (clinics.length === 0) {
    dispatch(rootActions.xtractAPI('clinic', 'search', { is_null: 'deleted_at' }))
  }
}, [])


  const currentClinicName = useMemo(() => {
    if (!clinics.length) return ''

    if (Number(clinicLocation) === -1) return 'All Locations'

    const matched = clinics.find(clinic =>
      Number(clinic.clinic_id) === Number(clinicLocation)
    )

    return matched?.name || ''
  }, [clinicLocation, clinics])

  return (
    <div className="dropdown dropdown-ribbon">
      <div
        className="btn btn-ribbon dropdown-toggle"
        type="button"
        id="screensDropDown"
        data-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="true"
        aria-label="Main menu dropdown"
        data-cy="mainMenuDropdown"
      >
        <div style={{ fontWeight: 'bold' }}>{username}
          <NotificationBadge />
          <span className="caret" />
        </div>
        {currentClinicName && (
          <div style={{ fontSize: '12px', color: '#fff' }}>{currentClinicName}</div>
        )}
      </div>
      <ul className="dropdown-menu dropdown-menu-right">
        <ScreenSubmenu
          screens={screens}
          disabled={inWorkflow}
          prefix={prefix}
        />
        <WindowSubmenu windows={windows} inWorkflow={inWorkflow} />
        <FavoritesSubmenu
          windows={windows}
          screens={screens}
          workflows={workflows}
          inWorkflow={inWorkflow}
          prefix={prefix}
        />
        <WorkflowSubmenu
          workflows={workflows}
          inWorkflow={inWorkflow}
          disabled={inWorkflow}
          prefix={prefix}
        />
        {(windows == null || windows.length === 0) &&
          (screens == null || screens.length === 0) ? null : (
          <li className="divider" />
        )}
        {context !== 'nurseDashboard' ? (
          <DashboardScreenSubmenu disabled={inWorkflow || dashboardDisabled} />
        ) : null}
        {adminMenuAllowed && context !== 'adminDisplay' ? (
          <AdminDisplayScreenSubmenu
            disabled={inWorkflow || adminDisplayDisabled}
          />
        )
          : null}
        {viewDesignModeSubmenu
          ? (
            <DesignModeSubmenu />
          )
          : null}
        <SetLocationSubMenu />
        <li
          className={
            (settingsDisabled || inWorkflow) === true ? 'disabled' : ''
          }
          data-toggle={settingsDisabled && isIpad ? 'tooltip' : null}
          data-title={'Settings are unavailable from iPad.'}
          data-placement="left"
          data-delay="0, 500"
        >
          <a
            role="button"
            aria-label="settings link"
            onClick={settingsDisabled ? null : configClick} // Prevent click when disabled
            data-cy="settings"
          >
            <FontAwesomeIcon icon={['fas', 'cogs']} />
            Settings
          </a>
        </li>
        <li className={inWorkflow === true ? 'disabled' : ''}>
          <a
            role="button"
            aria-label="user details link"
            onClick={userDetailsClick}
            data-cy="user-details"
          >
            <FontAwesomeIcon icon={['far', 'address-card']} />
            User details
          </a>
        </li>
        <li className={inWorkflow === true ? 'disabled' : ''}>
          <a
            role="button"
            aria-label="user message link"
            onClick={userMessagesClick}
            data-cy="user-message"
          >
            <FontAwesomeIcon icon={['far', 'envelope']} />
            Messages
            <NotificationBadge />
          </a>
        </li>
        <li className="divider" />
        <li className={inWorkflow === true ? 'disabled' : ''}>
          <a
            role="button"
            aria-label="lobby dashboard link"
            onClick={lobbyDashboardClick}
            data-cy="lobby-dashboard"
          >
            <FontAwesomeIcon icon={['far', 'tv']} />
            Lobby display
          </a>
        </li>
        <li className={inWorkflow === true ? 'disabled' : ''}>
          <a
            role="button"
            aria-label="lobby login link"
            onClick={lobbyLoginClick}
            data-cy="lobby-login"
          >
            <FontAwesomeIcon icon={['far', 'fingerprint']} />
            Lobby login app
          </a>
        </li>
        {externalLinks.length > 0 ? (
          <>
            <li className="divider" />
            <li className={inWorkflow === true ? 'disabled' : ''}>
              {externalLinks.map((link, idx) => {
                let icon = ['fas', 'link']
                if (Array.isArray(link.icon) && link.icon.length == 2) {
                  icon = link.icon
                }
                return (
                  <a
                    key={'link' + idx}
                    role="button"
                    aria-label="order supplies link"
                    onClick={e => externalLinkClick(e, link.url)}
                    data-cy="order-supplies"
                  >
                    <FontAwesomeIcon icon={icon} />
                    {link.name != null ? link.name : link.url}
                  </a>
                )
              })}
            </li>
          </>
        ) : null}
        <li className="divider" />
        <HelpSubmenu disabled={inWorkflow} />
        <li className="divider" />
        <li>
          <a
            role="button"
            aria-label="logout link"
            onClick={loginClick}
            data-cy="logout"
          >
            <FontAwesomeIcon icon={['fas', 'power-off']} />
            Logout
          </a>
        </li>
      </ul>
    </div>
  )
}

MainDropdown.propTypes = {
  // Should the dashboard menu be enabled or not
  dashboardDisabled: PropTypes.bool,
  inWorkflow: PropTypes.bool
}

export default MainDropdown
